/**
 * Theme store — persists to localStorage as 'castellan_theme'.
 * Controls accent, background, surface, border, and text colors.
 * Injects CSS variables onto document.documentElement so they're
 * available globally without needing prop-drilling.
 */
import { create } from 'zustand'

export interface Theme {
  id: string
  label: string
  accent: string       // gold / highlight color
  accentDim: string    // accent at low opacity for backgrounds
  bgDeep: string       // page background
  bgSurface: string    // cards / sidebar
  bgElevated: string   // active states / inputs
  border: string       // border color
  textPrimary: string  // main text
  textMuted: string    // secondary text
  textHint: string     // placeholders / hints
}

export const PRESETS: Theme[] = [
  {
    id: 'navy',
    label: 'Navy',
    accent:      '#c9a84c',
    accentDim:   'rgba(201,168,76,0.15)',
    bgDeep:      '#0f1a2e',
    bgSurface:   '#162033',
    bgElevated:  '#1e2d45',
    border:      '#1e3a5f',
    textPrimary: '#e2e8f0',
    textMuted:   '#64748b',
    textHint:    '#475569',
  },
  {
    id: 'midnight',
    label: 'Midnight',
    accent:      '#818cf8',
    accentDim:   'rgba(129,140,248,0.15)',
    bgDeep:      '#09090f',
    bgSurface:   '#111118',
    bgElevated:  '#1a1a2a',
    border:      '#2a2a40',
    textPrimary: '#e2e8f0',
    textMuted:   '#64748b',
    textHint:    '#4b5563',
  },
  {
    id: 'forest',
    label: 'Forest',
    accent:      '#4ade80',
    accentDim:   'rgba(74,222,128,0.15)',
    bgDeep:      '#0a1a0f',
    bgSurface:   '#0f2318',
    bgElevated:  '#163020',
    border:      '#1a4a28',
    textPrimary: '#e2e8f0',
    textMuted:   '#64748b',
    textHint:    '#475569',
  },
  {
    id: 'crimson',
    label: 'Crimson',
    accent:      '#f87171',
    accentDim:   'rgba(248,113,113,0.15)',
    bgDeep:      '#1a0a0a',
    bgSurface:   '#231010',
    bgElevated:  '#2d1515',
    border:      '#4a1a1a',
    textPrimary: '#e2e8f0',
    textMuted:   '#64748b',
    textHint:    '#475569',
  },
  {
    id: 'slate',
    label: 'Slate',
    accent:      '#94a3b8',
    accentDim:   'rgba(148,163,184,0.15)',
    bgDeep:      '#0f1117',
    bgSurface:   '#161b27',
    bgElevated:  '#1e2535',
    border:      '#2a3347',
    textPrimary: '#e2e8f0',
    textMuted:   '#64748b',
    textHint:    '#475569',
  },
  {
    id: 'copper',
    label: 'Copper',
    accent:      '#fb923c',
    accentDim:   'rgba(251,146,60,0.15)',
    bgDeep:      '#180e05',
    bgSurface:   '#1f1409',
    bgElevated:  '#2a1e0d',
    border:      '#3d2a10',
    textPrimary: '#e2e8f0',
    textMuted:   '#64748b',
    textHint:    '#475569',
  },
  {
    id: 'highcontrast',
    label: 'High Contrast',
    accent:      '#ffff00',
    accentDim:   'rgba(255,255,0,0.2)',
    bgDeep:      '#000000',
    bgSurface:   '#0a0a0a',
    bgElevated:  '#141414',
    border:      '#ffffff',
    textPrimary: '#ffffff',
    textMuted:   '#cccccc',
    textHint:    '#999999',
  },
]

export const DEFAULT_THEME = PRESETS[0]

function loadTheme(): Theme {
  try {
    const raw = localStorage.getItem('castellan_theme')
    if (!raw) return DEFAULT_THEME
    const saved = JSON.parse(raw)
    // Allow custom accent override on any preset
    const base = PRESETS.find(p => p.id === saved.id) ?? DEFAULT_THEME
    return saved.customAccent
      ? { ...base, accent: saved.customAccent, accentDim: hexToRgba(saved.customAccent, 0.15) }
      : base
  } catch { return DEFAULT_THEME }
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function applyTheme(t: Theme) {
  const root = document.documentElement
  root.style.setProperty('--accent',       t.accent)
  root.style.setProperty('--accent-dim',   t.accentDim)
  root.style.setProperty('--bg-deep',      t.bgDeep)
  root.style.setProperty('--bg-surface',   t.bgSurface)
  root.style.setProperty('--bg-elevated',  t.bgElevated)
  root.style.setProperty('--border',       t.border)
  root.style.setProperty('--text-primary', t.textPrimary)
  root.style.setProperty('--text-muted',   t.textMuted)
  root.style.setProperty('--text-hint',    t.textHint)
}

/**
 * Apply a tenant‑provided primary color on top of the current theme
 * without touching backgrounds / borders / text. The user's personal
 * theme choice (preset + optional custom accent) still wins; the tenant
 * primary is only used as the base accent when the user hasn't set one.
 *
 * Called from the tenant store after fetchConfig() resolves.
 */
export function applyTenantPrimary(primary: string | undefined) {
  if (!primary || !/^#[0-9A-Fa-f]{6}$/.test(primary)) return
  // Only apply if the user hasn't picked a custom accent
  const state = useThemeStore.getState()
  if (state.customAccent) return
  const base = { ...state.theme, accent: primary, accentDim: hexToRgba(primary, 0.15) }
  applyTheme(base)
  useThemeStore.setState({ theme: base })
}

interface ThemeState {
  theme: Theme
  customAccent: string
  setPreset: (id: string) => void
  setCustomAccent: (hex: string) => void
}

const initialTheme = loadTheme()
applyTheme(initialTheme)

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: initialTheme,
  customAccent: '',

  setPreset(id: string) {
    const base = PRESETS.find(p => p.id === id) ?? DEFAULT_THEME
    const customAccent = get().customAccent
    const t = customAccent
      ? { ...base, accent: customAccent, accentDim: hexToRgba(customAccent, 0.15) }
      : base
    applyTheme(t)
    localStorage.setItem('castellan_theme', JSON.stringify({ id, customAccent: customAccent || null }))
    set({ theme: t })
    // Notify dashboard (same tab) that theme changed
    try { window.dispatchEvent(new CustomEvent('castellan:theme-changed', { detail: t })) } catch { /* silent */ }
  },

  setCustomAccent(hex: string) {
    if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return
    const base = PRESETS.find(p => p.id === get().theme.id) ?? DEFAULT_THEME
    const t = { ...base, accent: hex, accentDim: hexToRgba(hex, 0.15) }
    applyTheme(t)
    localStorage.setItem('castellan_theme', JSON.stringify({ id: base.id, customAccent: hex }))
    set({ theme: t, customAccent: hex })
    try { window.dispatchEvent(new CustomEvent('castellan:theme-changed', { detail: t })) } catch { /* silent */ }
  },
}))
